/**
 * Smart Prefetching & Route Preloading
 * Netflix/Duolingo-style instant navigation
 * 
 * Prefetches routes and data in the background to make navigation feel instant
 */

import { logger } from './logger';

/**
 * Prefetch a route using <link rel="prefetch">
 * Browser will preload the route in the background
 */
export function prefetchRoute(url: string): void {
  if (typeof window === 'undefined') return;
  
  // Check if already prefetched
  const existingLink = document.querySelector(`link[rel="prefetch"][href="${url}"]`);
  if (existingLink) {
    logger.debug('Route already prefetched:', url);
    return;
  }

  // Create prefetch link (omit as="document" - Chrome/Edge don't support it for preload;
  // for prefetch it's optional and omitting avoids unsupported-value warnings)
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  
  // Add to head
  document.head.appendChild(link);
  
  logger.debug('Prefetched route:', url);
}

/**
 * Prefetch multiple routes
 */
export function prefetchRoutes(urls: string[]): void {
  urls.forEach(url => prefetchRoute(url));
}

/**
 * Preload a route - uses prefetch since Chrome/Edge don't support
 * <link rel="preload" as="document"> for HTML documents.
 * Prefetch works for route preloading without console warnings.
 */
export function preloadRoute(url: string): void {
  prefetchRoute(url);
}

/**
 * Prefetch route when user hovers over element (desktop)
 * or touches element (mobile)
 */
export function prefetchOnHover(
  element: HTMLElement,
  url: string,
  options?: { delay?: number }
): () => void {
  if (typeof window === 'undefined') return () => {};
  
  const delay = options?.delay || 100; // Default 100ms delay
  let timeoutId: NodeJS.Timeout | null = null;
  let prefetched = false;

  const prefetch = () => {
    if (prefetched) return;
    timeoutId = setTimeout(() => {
      prefetchRoute(url);
      prefetched = true;
    }, delay);
  };

  const cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  // Desktop: hover
  element.addEventListener('mouseenter', prefetch);
  element.addEventListener('mouseleave', cancel);

  // Mobile: touch start
  element.addEventListener('touchstart', prefetch, { passive: true });

  // Return cleanup function
  return () => {
    element.removeEventListener('mouseenter', prefetch);
    element.removeEventListener('mouseleave', cancel);
    element.removeEventListener('touchstart', prefetch);
    cancel();
  };
}

/**
 * Preload next test when current test starts
 * This makes the "Next Test" button feel instant
 */
export function preloadNextTest(currentTestId: string, getNextTestFn: () => { id: string; name: string } | null): void {
  if (typeof window === 'undefined') return;

  // Use requestIdleCallback to avoid blocking main thread
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      const nextTest = getNextTestFn();
      if (nextTest?.id) {
        const nextTestUrl = `/practice/${nextTest.id}`;
        preloadRoute(nextTestUrl); // Use preload (higher priority) for next test
        logger.debug('Preloaded next test:', nextTestUrl);
      }
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      const nextTest = getNextTestFn();
      if (nextTest?.id) {
        const nextTestUrl = `/practice/${nextTest.id}`;
        preloadRoute(nextTestUrl);
        logger.debug('Preloaded next test:', nextTestUrl);
      }
    }, 1000);
  }
}

/**
 * Prefetch all test routes (for TestsPage)
 * Prefetches in background when user is on tests page
 */
export function prefetchAllTestRoutes(testIds: string[]): void {
  if (typeof window === 'undefined') return;

  // Use requestIdleCallback to avoid blocking
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      testIds.forEach(testId => {
        const testUrl = `/practice/${testId}`;
        prefetchRoute(testUrl);
      });
      logger.debug('Prefetched all test routes:', testIds.length);
    }, { timeout: 3000 });
  } else {
    // Fallback: prefetch in batches
    let index = 0;
    const batchSize = 5;
    
    const prefetchBatch = () => {
      const batch = testIds.slice(index, index + batchSize);
      batch.forEach(testId => {
        const testUrl = `/practice/${testId}`;
        prefetchRoute(testUrl);
      });
      index += batchSize;
      
      if (index < testIds.length) {
        setTimeout(prefetchBatch, 500); // Wait 500ms between batches
      }
    };
    
    setTimeout(prefetchBatch, 1000);
  }
}

/**
 * Prefetch navigation routes (Dashboard, Tests, Settings, etc.)
 */
export function prefetchNavigationRoutes(): void {
  if (typeof window === 'undefined') return;

  const routes = [
    '/dashboard',
    '/tests',
    '/mock-exam',
    '/settings'
  ];

  // Use requestIdleCallback
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      routes.forEach(route => prefetchRoute(route));
      logger.debug('Prefetched navigation routes');
    }, { timeout: 2000 });
  } else {
    setTimeout(() => {
      routes.forEach(route => prefetchRoute(route));
    }, 1000);
  }
}






















